import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import api from '../../../api/axiosInterceptor';
import { API_BASE_URL } from '../../../config';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

// Fetch FE Base list for dropdown
const useFetchFeBaseList = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-base/`)
      .then(res => setList(res.data))
      .catch(() => setList([]));
  }, []);
  return list;
};

export const AdminFirMasterComponent = () => {
  const feBaseList = useFetchFeBaseList();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [formData, setFormData] = useState({
    financial_establishment: '',
    description: '',
    doc: null,
  });

  const [formErrors, setFormErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'financial_establishment', field: 'financial_establishment', headerName: 'FE Base', width: 200 },
    { id: 'description', field: 'description', headerName: 'Description', width: 300 },
    {
      id: 'doc',
      field: 'doc',
      headerName: 'Document',
      width: 120,
      renderCell: (params) =>
        params.value ? (
          <a href={params.value} target="_blank" rel="noopener noreferrer">
            View
          </a>
        ) : (
          'No Doc'
        ),
    },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={uploading}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={uploading}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  const fetchFirs = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/fe-mgmt/fir/`)
      .then(response => {
        const apiData = response.data.map((item, index) => {
          const feBaseObj = feBaseList.find(fe => fe.id === item.financial_establishment);
          return {
            id: item.id,
            slNo: index + 1,
            financial_establishment: feBaseObj ? feBaseObj.name_of_fe : item.financial_establishment,
            description: item.description,
            doc: item.doc,
          };
        });
        setRows(apiData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Failed to fetch FIRs:', error);
        setSnackbarMessage('Failed to load FIR data.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (feBaseList.length > 0) {
      fetchFirs();
    }
  }, [feBaseList]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleAddItem = () => {
    setIsEditMode(false);
    setFormData({
      financial_establishment: '',
      description: '',
      doc: null,
    });
    setFormErrors({});
    setUploadProgress(0);
    setOpenFormDialog(true);
  };

  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);

    // Find FE Base by name or by UUID to set correct id for form dropdown
    const feBaseObj = feBaseList.find(fe => fe.name_of_fe === item.financial_establishment || fe.id === item.financial_establishment);

    setFormData({
      financial_establishment: feBaseObj ? feBaseObj.id : '',
      description: item.description,
      doc: null,
    });

    setFormErrors({});
    setUploadProgress(0);
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/fe-mgmt/fir/${deleteItem.id}/`)
      .then(() => {
        setSnackbarMessage('FIR deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchFirs();
      })
      .catch(error => {
        console.error('Delete failed:', error);
        setSnackbarMessage('Failed to delete FIR.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteItem(null);
      });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.financial_establishment) errors.financial_establishment = 'FE Base is required';
    if (!formData.description?.trim()) errors.description = 'Description is required';
    if (!isEditMode && !formData.doc) errors.doc = 'Document upload is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setFormData({ ...formData, doc: e.target.files[0] });
    }
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;

    const payload = new FormData();
    payload.append('financial_establishment', formData.financial_establishment);
    payload.append('description', formData.description);
    if (formData.doc) {
      payload.append('doc', formData.doc);
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      if (isEditMode) {
        await api.patch(`/fe-mgmt/fir/${selectedItem.id}/`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (event) => {
            setUploadProgress(Math.round((event.loaded * 100) / event.total));
          },
        });
        setSnackbarMessage('FIR updated successfully.');
      } else {
        await api.post(`/fe-mgmt/fir/`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (event) => {
            setUploadProgress(Math.round((event.loaded * 100) / event.total));
          },
        });
        setSnackbarMessage('FIR added successfully.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchFirs();
      setOpenFormDialog(false);
    } catch (error) {
      console.error('Submit failed:', error);
      setSnackbarMessage('Failed to submit FIR.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (loading) return <LoadingSpinner size={30} />;

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2,
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center"
        bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">FIR Records</Typography>
        <Button variant="contained" color="primary" onClick={handleAddItem} disabled={uploading}>Add</Button>
      </Box>

      <TableComponent columns={columns} rows={rows} manageColumnStatus enableSort
        sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' }}} />

      <Dialog open={openFormDialog} onClose={() => !uploading && setOpenFormDialog(false)}>
        <DialogTitle>{isEditMode ? 'Edit FIR' : 'Add FIR'}</DialogTitle>
        <DialogContent>
          <TextField select margin="dense" label="FE Base" fullWidth
            value={formData.financial_establishment}
            error={!!formErrors.financial_establishment}
            helperText={formErrors.financial_establishment}
            onChange={e => setFormData({ ...formData, financial_establishment: e.target.value })}
            disabled={uploading}>
            {feBaseList.map(fe => <MenuItem key={fe.id} value={fe.id}>{fe.name_of_fe || fe.id}</MenuItem>)}
          </TextField>
          <TextField margin="dense" label="Description" multiline rows={3} fullWidth
            value={formData.description}
            error={!!formErrors.description}
            helperText={formErrors.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            disabled={uploading} />
          <Button variant="outlined" component="label" sx={{ mt: 2 }}>
            {formData.doc ? formData.doc.name : 'Upload Document'}
            <input type="file" hidden onChange={handleFileChange} disabled={uploading} />
          </Button>
          {formErrors.doc && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{formErrors.doc}</Typography>}
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>Uploading... {uploadProgress}%</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !uploading && setOpenFormDialog(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={uploading}>{isEditMode ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this FIR?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={uploading}>Delete</Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};
